/**
 * Bestand-Konverter: parst die Freitext-Bestand-Beschreibung (beide Kartierer-
 * Konventionen) in das strukturierte Modell und rendert den kanonischen,
 * SEA-konformen Text.
 *
 * Reine Funktion ohne Angular-Abhängigkeit — das Artenvokabular wird injiziert
 * (Default: Seed; Produktion: bpweb-listen-DB). Validiert gegen 3150 echte
 * Bestand-Zellen (siehe bestand-analyse/).
 */
import {
	BestandArt, BestandBeschreibung, BestandEbene, BestandSchicht, ParsedBestand,
} from './bestand-model';
import {
	BIOTOPE, CONNECT, LAYER_MARK, MIX, nf, QUAL, SCHLUSS, SEA_BH, SEA_STUFE,
	SPECIES_WORD, SpeciesIndex, STUFE, buildSpeciesIndex,
} from './bestand-vocabulary';

interface Tok { kind: string; value: string; }
interface IGroup { arten: BestandArt[]; quals: string[]; stufeMark?: string; stufeSeq?: string[]; schluss?: string; }

const CAPS = /^[A-ZÄÖÜ]{2,5}\d?$/;
const ST_AS_STANGENHOLZ = new Set(['St', 'St.', 'Sth', 'Sth.', 'STH']);

function atoms(seg: string): string[] {
	const out: string[] = [];
	// Trenner: Leerzeichen, Komma, Semikolon (DB-Konvention mancher Bearbeiter),
	// Doppelpunkt (Schicht-Marker „ÜH:") und Plus (Mischarten „GKI+RBU").
	for (const tok of seg.trim().split(/[\s,;:+]+/)) {
		if (!tok) continue;
		// Auch der Gedankenstrich „–" trennt: mit ihm schreibt der Konverter
		// selbst den Wuchsklassen-Bereich (z.B. „SCHBMH–STKBMH"). Ohne dieses
		// Trennen liefe der Bereich in die Notzerlegung splitGlued, die lange
		// Kürzel (6 Buchstaben) zerschneidet — der Re-Parse wäre kaputt.
		if (/[-–]/.test(tok) && tok !== '-' && tok !== '–') {
			const parts = tok.split(/[-–]/).filter(Boolean);
			parts.forEach((p, i) => { out.push(p); if (i < parts.length - 1) out.push('-'); });
		} else {
			out.push(tok);
		}
	}
	return out;
}

/** Zerlegt zusammengeklebte Tokens ('swBh', 'st.St.', 'imUstd') an Punkt-/CamelCase-Grenzen. */
function splitGlued(raw: string): string[] {
	const parts: string[] = [];
	for (const p of raw.split('.')) {
		if (!p) continue;
		const m = p.match(/[A-ZÄÖÜ]?[a-zäöü]+|[A-ZÄÖÜ]{2,5}\d?|[A-ZÄÖÜ]/g);
		if (m) parts.push(...m); else parts.push(p);
	}
	return parts.length ? parts : [raw];
}

function classify(raw: string, idx: SpeciesIndex): Tok {
	if (ST_AS_STANGENHOLZ.has(raw)) return { kind: 'stufe', value: 'St.' };   // Stangenholz (Groß-S)
	if (raw === 'st' || raw === 'st.') return { kind: 'qual', value: 'st.' };  // starkes (klein)
	const n = nf(raw);
	if (SCHLUSS[n]) return { kind: 'schluss', value: SCHLUSS[n] };
	if (QUAL[n]) return { kind: 'qual', value: QUAL[n] };
	if (STUFE[n]) return { kind: 'stufe', value: STUFE[n] };
	if (MIX[n]) return { kind: 'mix', value: MIX[n] };
	if (LAYER_MARK[n]) return { kind: 'layer', value: LAYER_MARK[n] };
	if (BIOTOPE.has(n)) return { kind: 'biotope', value: raw };
	if (SPECIES_WORD[n]) return { kind: 'species', value: SPECIES_WORD[n] };   // ausgeschriebene Art
	if (CAPS.test(raw)) {
		const r = idx.resolve(raw);
		return r.bekannt ? { kind: 'species', value: r.code } : { kind: 'species_unknown', value: raw };
	}
	if (CONNECT.has(n)) return { kind: 'connect', value: raw };
	const up = raw.toUpperCase();                              // schludrige Schreibung (GFi, Bi)
	if (/^[A-ZÄÖÜ]{2,5}$/.test(up)) {
		const r = idx.resolve(up);
		if (r.bekannt) return { kind: 'species', value: r.code };
	}
	return { kind: 'unknown', value: raw };
}

function expand(layer: string, idx: SpeciesIndex): Tok[] {
	const out: Tok[] = [];
	for (const tok of atoms(layer)) {
		if (tok === '-') { out.push({ kind: 'range', value: '-' }); continue; }
		const c = classify(tok, idx);
		if (c.kind === 'unknown' || c.kind === 'species_unknown') {
			const sub = splitGlued(tok);
			if (sub.length > 1) {
				const subc = sub.map(s => classify(s, idx));
				if (subc.every(x => x.kind !== 'unknown' && x.kind !== 'species_unknown')) {
					out.push(...subc); continue;
				}
			}
		}
		out.push(c);
	}
	return out;
}

/** Wuchsklassen einer Gruppe als SEA-Codes (z.B. ['SCHBMH'] oder Bereich ['SCHBMH','STKBMH']). */
function stufeToSea(quals: string[], mark: string | undefined): string[] {
	if (!mark) return [];
	if (mark === 'Bh.') {
		// Baumholz ohne Stärkeangabe wird als mittleres Baumholz angenommen
		// (fachliche Vorgabe — sonst käme der Hinweis viel zu oft).
		if (quals.length === 0) return ['MTLBMH'];
		return quals.map(q => SEA_BH[q] ?? 'MTLBMH');
	}
	const sea = SEA_STUFE[mark];
	return sea ? [sea] : [mark];
}

// Nur der Überhalt wird beschriftet; die übrigen Schichten stehen unbeschriftet
// in fester Reihenfolge, mit „/" getrennt (fachliche Vorgabe).
const EBENE_LABEL: Record<BestandEbene, string> = { ueh: 'ÜH', b1: '', b2: '', zwstd: '', ustd: '' };
const EBENE_ORDER: BestandEbene[] = ['ueh', 'b1', 'b2', 'zwstd', 'ustd'];

/**
 * Rendert die strukturierte Bestand-Beschreibung in den kanonischen SEA-Text
 * (Schichten in fester Reihenfolge, nur ÜH beschriftet, „/" zwischen Schichten,
 * Komma zwischen Art/Wuchsklasse/Schluss). Von Parser UND Eingabehilfe genutzt.
 */
export function renderBestand(b: BestandBeschreibung): string {
	const schichten = [...b.schichten].sort(
		(x, y) => EBENE_ORDER.indexOf(x.ebene) - EBENE_ORDER.indexOf(y.ebene),
	);
	const parts: string[] = [];
	for (const s of schichten) {
		const seg = s.gruppen.map(g => {
			const arten = g.arten.map(a => a.code).join('-');
			const stufe = (g.stufe?.wuchsklassen ?? []).join('–');
			return [arten, stufe, g.schluss].filter(Boolean).join(', ');
		}).filter(Boolean);
		let txt = seg.join(' / ');
		const label = EBENE_LABEL[s.ebene];
		if (label && txt) txt = `${label}: ${txt}`;
		if (txt) parts.push(txt);
	}
	for (const bio of b.biotope) parts.push(bio);
	return parts.filter(Boolean).join(' / ');
}

export interface ParseOptions { speciesIndex?: SpeciesIndex; }

export function parseBestand(text: string | undefined, opts: ParseOptions = {}): ParsedBestand {
	const idx = opts.speciesIndex ?? buildSpeciesIndex();
	const roh = (text ?? '').trim();
	const issues: string[] = [];
	let confidence = 1;
	const lower = (c: number) => { confidence = Math.min(confidence, c); };

	if (roh === '') {
		return { roh: '', kind: 'leer', bestand: { schichten: [], biotope: [] }, kanonisch: '', confidence: 1, issues: [] };
	}

	const ischichten: { ebene: BestandEbene; groups: IGroup[] }[] = [];
	const biotope: string[] = [];

	for (const layerText of roh.split('/')) {
		let cur: { ebene: BestandEbene; groups: IGroup[] } = { ebene: 'b1', groups: [] };
		const local = [cur];
		let group: IGroup = { arten: [], quals: [] };
		let pendingSchluss: string | undefined;
		let lastSchluss: string | undefined;
		let rangeMode = false;

		const pushGroup = () => {
			if (group.arten.length || group.stufeMark) {
				if (!group.schluss) group.schluss = lastSchluss;
				cur.groups.push(group);
			}
			group = { arten: [], quals: [] };
		};

		for (const t of expand(layerText, idx)) {
			if (t.kind === 'range') { rangeMode = true; continue; }
			switch (t.kind) {
				case 'layer': {
					pushGroup();
					cur = { ebene: t.value as BestandEbene, groups: [] };
					local.push(cur);
					pendingSchluss = undefined;
					break;
				}
				case 'species':
				case 'mix': {
					if (group.stufeMark && !rangeMode) {
						pushGroup();
						if (pendingSchluss) { group.schluss = pendingSchluss; pendingSchluss = undefined; }
					}
					if (pendingSchluss && group.arten.length === 0) { group.schluss = pendingSchluss; pendingSchluss = undefined; }
					group.arten.push({ code: t.value, bekannt: true });
					break;
				}
				case 'species_unknown': {
					issues.push(`Unbekanntes Baumart-Kürzel „${t.value}" – bitte prüfen oder ergänzen.`); lower(0.3);
					if (group.stufeMark && !rangeMode) pushGroup();
					group.arten.push({ code: t.value, bekannt: false, roh: t.value });
					break;
				}
				case 'qual': group.quals.push(t.value); break;
				case 'stufe': {
					// Mehrere Stufen-Tokens in derselben Gruppe = Wuchsklassen-Bereich
					// (entsteht beim Re-Parsen des kanonischen „SWBH–STBH"). Akkumulieren
					// statt überschreiben, sonst fiele der Bereich auf die letzte/obere
					// Grenze zusammen und der Konverter wäre nicht idempotent.
					if (group.stufeMark !== undefined) (group.stufeSeq ??= [group.stufeMark]).push(t.value);
					group.stufeMark = t.value;
					break;
				}
				case 'schluss': {
					if (rangeMode && (group.schluss || pendingSchluss)) {
						if (group.schluss) group.schluss += '-' + t.value;
						else pendingSchluss += '-' + t.value;
						lastSchluss = group.schluss ?? pendingSchluss;
					} else if (group.arten.length || group.stufeMark) {
						group.schluss = t.value; lastSchluss = t.value;
					} else { pendingSchluss = t.value; lastSchluss = t.value; }
					break;
				}
				case 'biotope': biotope.push(t.value); break;
				case 'connect': lower(0.85); break;
				default: issues.push(`Nicht erkannt: „${t.value}" – bitte prüfen.`); lower(0.4);
			}
			rangeMode = false;
		}
		pushGroup();
		for (const s of local) if (s.groups.length) ischichten.push(s);
	}

	// strukturiertes Modell bauen, dann zentral rendern (gleiche Logik wie Eingabehilfe)
	const schichten: BestandSchicht[] = ischichten.map(s => ({
		ebene: s.ebene,
		gruppen: s.groups.map(g => {
			// Wuchsklassen-Bereich (mehrere Stufen-Tokens) Stück für Stück mappen,
			// damit beide Grenzen erhalten bleiben (z.B. SWBH–STBH).
			const wk = g.stufeSeq
				? g.stufeSeq.flatMap(m => stufeToSea(g.quals, m))
				: stufeToSea(g.quals, g.stufeMark);
			return { arten: g.arten, stufe: wk.length ? { wuchsklassen: wk } : undefined, schluss: g.schluss };
		}),
	}));
	const hasTree = schichten.some(s => s.gruppen.length > 0);
	const beschreibung: BestandBeschreibung = { schichten, biotope };
	const kanonisch = renderBestand(beschreibung);
	let kind: ParsedBestand['kind'] = 'ok';
	if (!hasTree && biotope.length) { kind = 'offenland'; confidence = Math.max(confidence, 0.9); }
	else if (!hasTree) { kind = 'unklar'; lower(0.4); }

	return {
		roh, kind,
		bestand: beschreibung,
		kanonisch,
		confidence: Math.round(confidence * 100) / 100,
		issues,
	};
}

/** Minimaler Zielausschnitt eines ExtendedHeader (vermeidet Zyklus-Import). */
export interface BestandTarget {
	baumarten: unknown;
	bestand?: BestandBeschreibung;
	bestandFreitext?: string;
}

/**
 * Übernimmt ein Konvertierungs-Ergebnis in den Punkt: speichert die strukturierte
 * Beschreibung und – nur wenn dort kein Probestämme-Array steht – den kanonischen
 * SEA-Text in `baumarten` (für Export/PDF). Klobbert nie die Probestämme-Tabelle.
 */
export function applyBestand(target: BestandTarget, parsed: ParsedBestand): void {
	// Originaltext (vor der ersten Konvertierung) erhalten → Rückgängig jederzeit möglich.
	const original = target.bestand?.original ?? parsed.roh;
	target.bestand = { ...parsed.bestand, original };
	// Original-Prosa zusätzlich im Freitext-Feld bewahren, wenn dort noch nichts
	// steht und tatsächlich umgeschrieben wurde (Prosa ≠ kanonisch).
	if (!target.bestandFreitext && parsed.roh.trim() && parsed.roh.trim() !== parsed.kanonisch) {
		target.bestandFreitext = parsed.roh.trim();
	}
	if (typeof target.baumarten === 'string' || target.baumarten == null) {
		target.baumarten = parsed.kanonisch;
	}
}

/** Prüft an einem bereits geparsten Ergebnis, ob es SEA-konform ist (keine
 *  offenen Hinweise, eindeutig erkannt, Text ändert sich beim Konvertieren nicht
 *  mehr). Separat, damit UI-Komponente und Validator dieselbe Regel nutzen. */
export function istParsedSeaKonform(r: ParsedBestand): boolean {
	if (r.roh === '') return true; // leer ist nicht zu beanstanden
	return r.issues.length === 0 && r.kind !== 'unklar' && r.kanonisch === r.roh;
}

/** Ein Bestand-Text gilt als SEA-konform, wenn er keine offenen Hinweise hat
 *  und sich beim Konvertieren nicht mehr ändern würde. */
export function istBestandSeaKonform(text: string | undefined): boolean {
	const t = (text ?? '').trim();
	if (!t) return true; // leer ist nicht zu beanstanden
	return istParsedSeaKonform(parseBestand(t));
}

/**
 * „Bestand ok" für die Validierung: entweder SEA-konform ODER die (bewusst
 * abweichende) Beschreibung wurde vom Nutzer exakt so bestätigt. Die Bestätigung
 * ist an genau diesen Text gebunden — ändert der Nutzer den Bestand später, ist
 * `bestaetigt` ungleich dem neuen Text und die Bestätigung greift nicht mehr.
 */
export function istBestandOk(text: string | undefined, bestaetigt?: string): boolean {
	if (istBestandSeaKonform(text)) return true;
	const t = (text ?? '').trim();
	return t !== '' && bestaetigt != null && bestaetigt.trim() === t;
}

/**
 * Import-Behandlung: sicher erkannte Bestände (keine offenen Hinweise) werden
 * direkt in SEA-Form konvertiert; unsichere bleiben als Rohtext stehen (der
 * „Bestand"-Validator markiert sie dann gelb). Der Originaltext wird in jedem
 * Fall im Freitext-Feld bewahrt (verlustfrei). Gibt true zurück, wenn konvertiert.
 */
export function convertOnImport(target: BestandTarget): boolean {
	if (typeof target.baumarten !== 'string') return false;
	const text = target.baumarten.trim();
	if (!text) return false;
	const r = parseBestand(text);
	const sicher = r.issues.length === 0 && (r.kind === 'ok' || r.kind === 'offenland');
	if (sicher) {
		applyBestand(target, r);   // schreibt SEA-Text + Struktur + Original ins Freitext
		return true;
	}
	if (!target.bestandFreitext) target.bestandFreitext = text; // Original bewahren
	return false;
}

/**
 * Macht eine Konvertierung rückgängig: stellt den gespeicherten Originaltext
 * wieder her und entfernt die strukturierte Beschreibung. Gibt true zurück,
 * wenn etwas wiederhergestellt wurde.
 */
export function revertBestand(target: BestandTarget): boolean {
	const original = target.bestand?.original;
	if (original == null) return false;
	if (typeof target.baumarten === 'string' || target.baumarten == null) {
		target.baumarten = original;
	}
	target.bestand = undefined;
	return true;
}
